import React from 'react'
import { DragHandleDotsIcon } from '@/assets/icons/DragHandleDotsIcon'
import { useDragHandle } from '@/designs/new/components/atom/sortableSection/SortableCard'
import { ArrowUpRight } from 'lucide-react'

type MetricItem = {
  label: string
  value: number | string
  color?: string
  onClick?: () => void
}

type SummaryCardProps = {
  title: string
  metrics: MetricItem[]
  filterValue?: string
  onFilterChange?: (value: string) => void
  filterOptions?: string[]
  className?: string
  footerNode?: React.ReactNode
  dragDrop?: boolean
}

const WidgetX = ({
  title,
  metrics,
  filterValue = '30 days',
  onFilterChange,
  filterOptions = ['7 days', '30 days', '90 days'],
  className = '',
  footerNode,
  dragDrop,
}: SummaryCardProps) => {
  const { listeners, attributes } = useDragHandle()

  return (
    <div
      className={`group relative flex w-full flex-col rounded-xl border border-border border-l-[4px] border-l-accent bg-primary p-3 shadow-sm ${className}`}
    >
      {dragDrop && (
        <div
          {...listeners}
          {...attributes}
          className='absolute p-1 top-2 left-1/2 z-20 -translate-x-1/2 cursor-grab touch-none opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing'
        >
          <DragHandleDotsIcon />
        </div>
      )}

      {/* Header */}
      <div className='mb-6 flex items-center justify-between'>
        <h3 className='text-kpi-heading-2 font-semibold text-primary-foreground'>
          {title}
        </h3>

        {filterOptions && filterOptions.length > 0 && (
          <select
            value={filterValue}
            onChange={e => onFilterChange?.(e.target.value)}
            className='rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-kpi-supporting-1 text-gray-700 outline-none transition focus:border-gray-400'
          >
            {filterOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Metrics */}
      <div className='flex flex-1 items-start justify-between gap-6'>
        {metrics.map((item, index) => (
          <button
            onClick={item.onClick}
            key={index}
            className='group/drill flex w-fit flex-col items-start rounded-md px-1 py-1 text-left transition-all'
          >
            <span
              className='mb-1 text-kpi-primary-value font-semibold leading-none tracking-tight transition-all duration-200 group-hover/drill:-translate-y-[1px] group-hover/drill:opacity-90'
              style={{
                color: item.color || '#111827',
              }}
            >
              {item.value}
            </span>
            <span className='inline-flex items-center gap-1.5 text-kpi-supporting-1 font-medium'>
              <span className='relative overflow-hidden'>
                <span>{item.label}</span>

                <span className='absolute bottom-0 left-0 h-[1.5px] w-0 bg-current transition-all duration-150 ease-out group-hover/drill:w-full' />
              </span>

              <ArrowUpRight className='h-4 w-4 transition-transform duration-200 group-hover/drill:translate-x-[2px] group-hover/drill:-translate-y-[2px]' />
            </span>
          </button>
        ))}
      </div>

      {footerNode && (
        <>
          <div className='mt-2 mb-3 h-[1px] w-full bg-border' />
          <div className='flex items-center'>{footerNode}</div>
        </>
      )}
    </div>
  )
}

export default WidgetX
